import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import "./Login.css";

import Modal from "../components/Modal";
import { authAPI } from "../services/api";

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot Password State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: Verify, 2: Reset
  const [resetEmail, setResetEmail] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }

    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate("/", { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Demo login for testing
  const handleDemoLogin = () => {
    setEmail("demo@rfconference.com");
    setPassword("demo123");
  };

  // Forgot Password Handlers
  const handleVerifyCredentials = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetLoading(true);

    try {
      await authAPI.verifyResetCredentials(resetEmail, resetPhone);
      setResetStep(2); // Move to next step
    } catch (err) {
      setResetError(
        err.response?.data?.detail ||
          "Verification failed. Check your details.",
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetLoading(true);

    if (!newPassword || newPassword.length < 6) {
      setResetError("Password must be at least 6 characters");
      setResetLoading(false);
      return;
    }

    try {
      await authAPI.resetPassword(resetEmail, resetPhone, newPassword);
      setResetSuccess(true);
      setTimeout(() => {
        closeForgotModal();
      }, 2000);
    } catch (err) {
      setResetError(err.response?.data?.detail || "Failed to reset password.");
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setResetStep(1);
    setResetEmail("");
    setResetPhone("");
    setNewPassword("");
    setResetError("");
    setResetSuccess(false);
  };

  return (
    <div className="login-page">
      <div className="login-hero">
        <div className="hero-content">
          <div className="logo-box">
            <img
              src="/logo.png"
              alt="RF Logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>

          <p>Kolkata Regional Meet Registration-June 2026</p>
        </div>
        <svg
          className="hero-wave"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,64 C480,150 960,-20 1440,64 L1440,120 L0,120 Z"
            fill="var(--rf-gray-50)"
          />
        </svg>
      </div>

      <div className="login-form-container">
        <div className="login-card">
          <h2>Sign In</h2>
          <p className="login-subtitle">Enter your credentials</p>

          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  placeholder=".."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label htmlFor="password" style={{ marginBottom: 0 }}>
                  Password
                </label>
                <button
                  type="button"
                  className="demo-link"
                  onClick={() => setShowForgotModal(true)}
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "var(--rf-primary)",
                  }}
                >
                  Forgot Password?
                </button>
              </div>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type="password"
                  id="password"
                  placeholder=".."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? (
                <span className="btn-loading"></span>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                </>
              )}
            </button>

            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <span style={{ color: "var(--rf-gray-600)", fontSize: "14px" }}>
                New here?
              </span>
              <Link
                to="/register"
                className="demo-link"
                style={{ display: "inline", marginLeft: "8px" }}
              >
                Create an account
              </Link>
            </div>
          </form>

          {/* <button
            className="demo-link"
            onClick={handleDemoLogin}
            style={{ marginTop: "12px" }}
          >
            Use demo credentials
          </button> */}
        </div>

        <p className="help-text">
          Can't sign in? Contact the registration desk for assistance.
        </p>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotModal}
        onClose={closeForgotModal}
        title="Reset Password"
      >
        <div style={{ padding: "8px 4px" }}>
          {resetSuccess ? (
            <div
              style={{
                textAlign: "center",
                color: "var(--rf-success)",
                padding: "20px",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
              <h3>Password Changed!</h3>
              <p>You can now login with your new password.</p>
            </div>
          ) : (
            <>
              {resetError && (
                <div className="error-message" style={{ marginBottom: "16px" }}>
                  <AlertCircle size={16} />
                  <span>{resetError}</span>
                </div>
              )}

              {resetStep === 1 ? (
                <form onSubmit={handleVerifyCredentials}>
                  <p
                    style={{
                      marginBottom: "20px",
                      color: "var(--rf-gray-600)",
                    }}
                  >
                    Enter your registered email and phone number to verify your
                    identity.
                  </p>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={resetPhone}
                      onChange={(e) => setResetPhone(e.target.value)}
                      placeholder="Enter registered phone number"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Verifying..." : "Verify Identity"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword}>
                  <p
                    style={{
                      marginBottom: "20px",
                      color: "var(--rf-gray-600)",
                    }}
                  >
                    Identity verified. Please enter your new password.
                  </p>
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Updating..." : "Update Password"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Login;
